import React, { useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import {
  Button,
  Card,
  Grid,
  Group,
  Title,
  Flex,
  Checkbox,
  Box,
  ActionIcon,
} from "@mantine/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { shallow } from "zustand/shallow";
import { useTranslation } from "react-i18next";
import EffortBuilderForm from "./EffortBuilderForm";
import EffortFieldReorder from "./EffortFieldReorder";
import EffortFields from "./EffortFields";
import { PublishButton } from "@components/Effort/EffortStatus";
import { SaveFormButton } from "@components/Effort/EffortSavedForms";
import { generateEffortFieldRequest } from "./helpers";

import useAppStore from "./effort-builder-store";
import { appLanguages as supportedLanguages } from "@lib/appLanguages";

export default function EffortBuilder({
  effortId,
  fetchEffortData,
  showPublish,
  effortName,
  language,
  postmarkTemplateNames,
  entityName,
}) {
  const queryClient = useQueryClient();
  const [config, setConfig] = useState({});
  // const [previewLanguage, setPreviewLanguage] = useState("");
  const [eiWithTriggerId, setEiWithTriggerId] = useState(null);
  const { t, i18n } = useTranslation();
  const { effortState, setEffortState, fields, languages } = useAppStore(
    (state) => ({
      effortState: state.effortState,
      setEffortState: state.setEffortState,
      fields: state.effortState.fields,
      languages: state.effortState.languages,
    }),
    shallow
  );

  const queryKey = `effort${effortId}Configuration`;
  const eiWithTriggerQueryKey = `eiWithTrigger${effortId}`;

  const fetchData = () => queryClient.invalidateQueries([queryKey]);
  const fetchEiWithTrigger = () =>
    queryClient.invalidateQueries([eiWithTriggerQueryKey]);

  const { triggerFailureReason, data: triggerData } = useQuery({
    queryKey: [eiWithTriggerQueryKey],
    queryFn: async () =>
      axios
        .post(`/efforts/${effortId}/data-grabber/`, {
          context: "cond_reg_engage_item",
        })
        .then(({ data }) => {
          setEiWithTriggerId(data.response[0].engagement_item_id || null);
          return data.response[0];
        })
        .catch((err) => {
          setEiWithTriggerId(null);
          throw err;
        }),
  });

  const { failureReason, data: info } = useQuery({
    queryKey: [queryKey],
    queryFn: async () =>
      axios
        .get(`/efforts/${effortId}/configuration/`)
        .then(({ data }) => {
          const draftConfig = data.response[0];

          if (!draftConfig) return;

          setConfig(draftConfig);

          if (!draftConfig.fields || !draftConfig.fields.length) {
            setEffortState({
              title: "",
              description: "",
              fields: [],
              languages: ["en"],
            });
          } else {
            setEffortState({
              title: "",
              description: "",
              fields: draftConfig.fields,
              // make sure the languages are supported
              languages:
                draftConfig.languages && draftConfig.languages.length > 0
                  ? draftConfig.languages.includes("en")
                    ? draftConfig.languages.filter((f) =>
                        supportedLanguages.some((l) => l.language === f)
                      )
                    : [
                        ...draftConfig.languages.filter((f) =>
                          supportedLanguages.some((l) => l.language === f)
                        ),
                        "en",
                      ]
                  : ["en"],
            });
          }

          return data.response[0];
        })
        .catch((err) => {
          throw err;
        }),
  });

  function onUpdate(newState) {
    setEffortState({ ...effortState, ...newState });
    updateConfig({ ...effortState, ...newState });
  }

  function updateConfig(newConfig = effortState) {
    const req = {
      configuration: { ...config, ...newConfig },
    };

    axios
      .put(`/efforts/${effortId}/update-config/`, req)
      .then(() => {
        toast.success(t("common:saved") + "!");
        fetchEffortData();
        fetchData();
        fetchEiWithTrigger();
      })
      .catch((err) => {
        toast.error(err);
      });
  }

  function onFormSubmit(e) {
    generateEffortFieldRequest(
      fields,
      e,
      {
        first_name: "John",
        last_name: "Smith",
        id: "123-abc",
      },
      true
    ).then((newReq) => {
      console.log(newReq);
    });
  }

  function onLanguageChange(language) {
    if (!effortState.languages) {
      updateConfig({
        ...effortState,
        languages: [language],
      });
      return;
    }
    // make sure at least one language is selected
    const newLanguages = effortState.languages.includes(language)
      ? effortState.languages.filter((l) => l !== language)
      : [...effortState.languages, language];
    if (newLanguages.length === 0) {
      toast.error(t("mixer:effort_builder:minimum_languages_required"));
      return;
    }

    updateConfig({
      ...effortState,
      languages: newLanguages,
    });
  }

  const previewLanguages = supportedLanguages.filter((m) =>
    effortState.languages?.includes(m.language)
  );

  return (
    <React.Fragment>
      <Box mb="xl">
        <Title order={4} mb="sm">
          {t("mixer:effort_builder:supported_languages")}
        </Title>
        <Flex gap="sm" direction={{ base: "column", md: "row" }}>
          {supportedLanguages.map((m) => (
            <Checkbox
              size="sm"
              label={m.label}
              onChange={(e) => onLanguageChange(m.language)}
              checked={effortState.languages?.includes(m.language)}
              disabled={m.language === "en"}
            />
          ))}
        </Flex>
      </Box>
      <Grid>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <div>
            <EffortFields
              effortId={effortId}
              onChange={(newFields) => onUpdate({ fields: newFields })}
              updateConfig={updateConfig}
              postmarkTemplateNames={postmarkTemplateNames}
              eiWithTriggerId={eiWithTriggerId}
              entityName={entityName}
              effortName={effortName}
            />
          </div>
          <Group mt="lg">
            <SaveFormButton effortName={effortName} fields={fields} />
            <EffortFieldReorder
              onUpdate={(newFields) => onUpdate({ fields: newFields })}
            />
            <Button onClick={() => updateConfig()} color="green">
              {t("common:save")}
            </Button>
            {showPublish && (
              <PublishButton
                buttonProps={{
                  size: "sm",
                  variant: "light",
                  color: "green",
                }}
                effortId={effortId}
                onSuccess={() => true}
              />
            )}
          </Group>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Flex gap="xs" mb="sm">
            <Title order={3}>{t("common:preview")}</Title>
            {previewLanguages.length > 1 && (
              <>
                {previewLanguages.map((m) => (
                  <ActionIcon
                    onClick={() => {
                      // setPreviewLanguage(m.language);
                      i18n.changeLanguage(m.language);
                    }}
                    variant="light"
                    color={m.language === i18n.language ? "blue" : "gray"}
                    radius="xl"
                    size="md"
                  >
                    {m.flag}
                  </ActionIcon>
                ))}
              </>
            )}
          </Flex>
          <Card style={{ overflow: "visible" }}>
            <EffortBuilderForm
              languages={effortState.languages}
              // language={language}
              language={i18n.language}
              fields={fields}
              onSubmit={(e) => {
                onFormSubmit(e);
              }}
            />
          </Card>
        </Grid.Col>
      </Grid>
    </React.Fragment>
  );
}
